if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      variantRequestId = 0;
      serverSyncTimer = undefined;
      pendingServerSync = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        if (!shouldSwapProduct) {
          const isLetterPendant = this.variantSelectors?.dataset.letterPendant === 'true';

          if (isLetterPendant) {
            // Single source of truth: the builder's syncFromInput applies the
            // full instant update itself (variant id, price, button, media,
            // URL). Running applyInstantVariantUpdate here as well would
            // double-fire price/media/URL/pubsub for one click.
            const letterBuilder = this.variantSelectors?.querySelector('letter-pendant-builder');
            letterBuilder?.syncFromInput?.();
          } else {
            const variant = this.variantSelectors?.getCurrentVariant?.();

            if (variant) {
              this.applyInstantVariantUpdate(variant, productUrl);
            } else {
              this.applyUnavailableInstantUpdate(productUrl);
            }
          }
        }

        const letterInput = this.variantSelectors?.querySelector('[data-letter-input]');
        const isLetterInputFocused = letterInput && document.activeElement === letterInput;

        if (isLetterInputFocused) return;

        this.scheduleServerSync({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleBackgroundSync(productUrl),
          immediate: shouldSwapProduct,
        });
      }

      scheduleServerSync({ requestUrl, targetId, callback, immediate = false }) {
        clearTimeout(this.serverSyncTimer);
        // A new selection supersedes any in-flight sync. Abort it (and bump
        // the request id for a response already past the network stage) so a
        // slow stale response can never land on top of the newer state.
        this.abortController?.abort();
        this.variantRequestId++;
        this.pendingServerSync = { requestUrl, targetId, callback };

        if (immediate) {
          const pending = this.pendingServerSync;
          this.pendingServerSync = null;
          this.renderProductInfo(pending);
          return;
        }

        this.serverSyncTimer = setTimeout(() => {
          const pending = this.pendingServerSync;
          this.pendingServerSync = null;
          if (pending) this.renderProductInfo(pending);
        }, 350);
      }

      formatPdpMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const units = cents / 100;

        // Group thousands the way Liquid money filters do ("," styles use
        // "1,234.56"; "comma separator" styles use "1.234,56").
        const group = (decimals, thousands, decimal) => {
          const [whole, fraction] = units.toFixed(decimals).split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fraction ? `${grouped}${decimal}${fraction}` : grouped;
        };

        // The PDP price renders with money_without_trailing_zeros: keep
        // non-zero cents, drop them entirely when they are ".00".
        const withoutTrailingZeros = (thousands, decimal) => {
          const formatted = group(2, thousands, decimal);
          return formatted.endsWith(`${decimal}00`) ? formatted.slice(0, -3) : formatted;
        };

        return format
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, group(0, '.', ','))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withoutTrailingZeros('.', ','))
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, group(0, ',', '.'))
          .replace(/\{\{\s*amount\s*\}\}/g, withoutTrailingZeros(',', '.'));
      }

      updatePriceFromVariant(variant) {
        const priceRoot = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceRoot) return;

        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceRoot.classList.toggle('price--on-sale', !!onSale);
        priceRoot.classList.toggle('price--sold-out', !variant.available);
        priceRoot.classList.remove('hidden');

        if (onSale) {
          const compareEl = priceRoot.querySelector('.price__sale s.price-item--regular');
          const saleEl = priceRoot.querySelector('.price-item--sale');
          if (compareEl) compareEl.textContent = this.formatPdpMoney(variant.compare_at_price);
          if (saleEl) saleEl.textContent = this.formatPdpMoney(variant.price);
        } else {
          const regularEl = priceRoot.querySelector('.price__regular > .price-item--regular');
          if (regularEl) regularEl.textContent = this.formatPdpMoney(variant.price);
        }
      }

      // The monthly figure follows the price, and the whole row drops away
      // below the threshold where it is worth stating.
      updateFinancingFromVariant(variant) {
        const row = this.querySelector('[data-product-financing]');
        if (!row) return;

        const floor = parseInt(row.dataset.floorCents, 10) || 0;
        const months = parseInt(row.dataset.termMonths, 10) || 12;

        row.hidden = variant.price < floor;
        if (row.hidden) return;

        const amount = row.querySelector('[data-financing-amount]');
        if (amount) amount.textContent = this.formatPdpMoney(Math.floor(variant.price / months));
      }

      updateFulfillmentBadgeFromVariant(variant) {
        const badge = this.querySelector(`#FulfillmentBadge-${this.dataset.section}`);
        const textEl = badge?.querySelector('.product-fulfillment-badge__text');
        if (!badge || !textEl) return;

        const isInStock = (badge.dataset.inStockVariantIds || '').includes(`,${variant.id},`);
        const nextText = isInStock ? badge.dataset.textInStock : badge.dataset.textMadeToOrder;
        if (nextText && textEl.textContent.trim() !== nextText) textEl.textContent = nextText;

        // The two states look different, not just read differently.
        badge.classList.toggle('product-fulfillment-badge--in-stock', isInStock);
        badge.classList.toggle('product-fulfillment-badge--made-to-order', !isInStock);

        // Required Shop Pay Installments long-fulfillment notice follows the
        // badge state: visible only for made-to-order (4 week) variants.
        const note = this.querySelector('[data-installments-fulfillment-note]');
        if (note) note.hidden = isInStock;

        // Shipping & Returns drawer reads the same boolean. Retexting in place
        // (rather than re-rendering the accordion) keeps an open drawer open
        // and avoids a collapse/expand flash mid-read.
        const shipping = this.querySelector('[data-shipping-returns-copy]');
        if (shipping) {
          const nextCopy = isInStock ? shipping.dataset.textInStock : shipping.dataset.textMadeToOrder;
          if (nextCopy && shipping.textContent.trim() !== nextCopy) shipping.textContent = nextCopy;
        }
      }

      applyInstantVariantUpdate(variant, productUrl, { updateUrl = true } = {}) {
        this.variantSelectors?.updateSelectedVariant?.(variant);

        const mediaGallery = this.querySelector('media-gallery');
        const featuredMediaKey = variant.featured_media
          ? `${this.dataset.section}-${variant.featured_media.id}`
          : null;

        mediaGallery?.setVisibleVariant?.(variant.id, featuredMediaKey);

        this.updatePriceFromVariant(variant);
        this.updateFinancingFromVariant(variant);
        this.updateFulfillmentBadgeFromVariant(variant);
        // updateUrl: false lets high-frequency callers (letter pendant typing)
        // skip history.replaceState per keystroke; they debounce it instead.
        if (updateUrl) this.updateURL(productUrl, variant.id);
        this.updateVariantInputs(variant.id);

        if (!variant.available) {
          this.productForm?.toggleSubmitButton(true, window.variantStrings.soldOut);
        } else {
          this.productForm?.toggleSubmitButton(false);
        }

        // NOTE: instant (client-side) updates publish html: null because no
        // server render exists. In-theme subscribers only read sectionId and
        // variant; any subscriber reading html must null-check it (Dawn's own
        // server-sync publish sends a parsed document instead).
        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html: null,
            variant,
          },
        });
      }

      applyUnavailableInstantUpdate(productUrl) {
        this.updateURL(productUrl);
        this.setUnavailable();
      }

      resetProductFormState() {
        this.productForm?.handleErrorMessage();
      }

      setVariantLoading(isLoading) {
        this.classList.toggle('is-variant-loading', isLoading);
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const requestId = ++this.variantRequestId;
        const { signal } = this.abortController;

        fetch(requestUrl, { signal })
          .then((response) => {
            // A non-OK section render (429/5xx) returns an HTML error page that
            // parses fine but resolves no variant, which would otherwise flip a
            // valid selection to "Unavailable". Fail here so the client-applied
            // instant update is left untouched.
            if (!response.ok) throw new Error(`Variant section render failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (requestId !== this.variantRequestId) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            if (requestId !== this.variantRequestId) return;
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error(error);
          });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (!variantSelects || !this.variantSelectors) return;

        // Letter pendant guard: replacing the picker HTML recreates the
        // builder with an empty input, wiping typed letters and their line
        // item properties. Skip while the customer is typing (focus) AND
        // whenever the input holds a value — the client-side picker state is
        // authoritative once letters exist. Standard PDPs have no
        // [data-letter-input] and always take the replacement path.
        const letterInput = this.variantSelectors.querySelector('[data-letter-input]');
        if (letterInput && (document.activeElement === letterInput || letterInput.value.trim() !== '')) return;

        HTMLUpdateUtility.setInnerHTML(this.variantSelectors, variantSelects.innerHTML);
        this.variantSelectors.refreshFromServer?.();
      }

      handleBackgroundSync(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const currentVariant = this.variantSelectors?.getCurrentVariant?.();

          if (!variant || !currentVariant || String(variant.id) !== String(currentVariant.id)) {
            // Freshness check for the letter pendant: a response whose
            // selected variant no longer matches the client's current variant
            // is stale (e.g. the customer typed more letters while the fetch
            // was in flight). Once letters are typed, the client state
            // (sanitized input + matched variant + line item properties) is
            // authoritative — running the full update here would overwrite the
            // newer variant and wipe the typed letters via picker replacement.
            const letterInput = this.variantSelectors?.querySelector('[data-letter-input]');
            if (letterInput && letterInput.value.trim() !== '') return;

            // The server resolved no variant but the client still holds a
            // valid one: this is an error page or a section render that didn't
            // echo the selection (see the opening-variant fallback in
            // main-product.liquid). It is not authoritative — keep the client
            // state instead of flipping the product to "Unavailable".
            if (!variant && currentVariant) return;

            // When both client and server resolved a variant but they
            // disagree, the response is stale — a newer selection happened
            // while it was in flight. Discard it; the newer selection's own
            // sync is already scheduled and will deliver the right state.
            // (When either side is unresolved, the server render is
            // authoritative and we fall through to the full update.)
            if (variant && currentVariant) return;

            this.handleUpdateProductInfo(productUrl)(html);
            return;
          }

          this.syncSecondaryVariantFields(html, variant);
        };
      }

      syncSecondaryVariantFields(html, variant) {
        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('FulfillmentBadge');
        updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
        updateSourceFromDestination('Volume');
        updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

        // Belt-and-braces: re-align the primary fields with the confirmed
        // variant in case an earlier stale write left them out of sync. All
        // three calls are no-ops when the state is already correct.
        this.updateVariantInputs(variant.id);
        this.updatePriceFromVariant(variant);
        // The badge above was replaced from the server render, but the
        // Shipping & Returns drawer is retexted rather than re-rendered, so it
        // still needs this pass. No-op for the badge itself.
        this.updateFulfillmentBadgeFromVariant(variant);
        const mediaGallery = this.querySelector('media-gallery');
        const featuredMediaKey = variant.featured_media
          ? `${this.dataset.section}-${variant.featured_media.id}`
          : null;
        mediaGallery?.setVisibleVariant?.(variant.id, featuredMediaKey);

        this.pickupAvailability?.update(variant);
        this.updateQuantityRules(this.sectionId, html);
        this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
        this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const usesVariantGrid =
            this.querySelector('media-gallery')?.dataset.desktopLayout === 'variant_grid';

          if (!variant) {
            this.updateOptionValues(html);
            this.pickupAvailability?.update(variant);
            this.updateURL(productUrl, variant?.id);
            this.updateVariantInputs(variant?.id);
            this.setUnavailable();
            return;
          }

          if (usesVariantGrid) {
            this.applyInstantVariantUpdate(variant, productUrl);
            this.syncSecondaryVariantFields(html, variant);
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('FulfillmentBadge');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateOptionValues(html);
          this.pickupAvailability?.update(variant);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          // Shipping & Returns is retexted, not re-rendered, so it is not
          // covered by the replacements above.
          this.updateFulfillmentBadgeFromVariant(variant);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          const nextValue = variantId ?? '';
          if (input.value === String(nextValue)) return;
          input.value = nextValue;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;

        try {
          window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
        } catch (error) {
          // Safari rate-limits frequent history updates and throws a
          // SecurityError; swallow it — the URL catches up on the next change.
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateVariantGridMedia(sourceList, destinationList) {
        const existingMap = new Map(
          Array.from(sourceList.querySelectorAll('li[data-media-id]')).map((item) => [item.dataset.mediaId, item])
        );
        const destinationItems = Array.from(destinationList.querySelectorAll('li[data-media-id]'));
        const nextNodes = destinationItems.map((destinationItem, index) => {
          const existingItem = existingMap.get(destinationItem.dataset.mediaId);

          if (existingItem) {
            existingItem.className = destinationItem.className;
            return existingItem;
          }

          const newItem = destinationItem.cloneNode(true);
          if (index === 0) {
            newItem.querySelectorAll('img[loading="lazy"]').forEach((image) => {
              image.loading = 'eager';
            });
          }
          return newItem;
        });

        sourceList.replaceChildren(...nextNodes);
      }

      updateMedia(html, variantFeaturedMediaId) {
        const mediaGallery = this.querySelector('media-gallery');
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        if (
          mediaGallery?.dataset.desktopLayout === 'variant_grid' &&
          mediaGallerySource &&
          mediaGalleryDestination
        ) {
          this.updateVariantGridMedia(mediaGallerySource, mediaGalleryDestination);
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);

          mediaGallery.resetVariantGridCarousel?.();
          return;
        }

        if (!variantFeaturedMediaId) return;

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
